import { VibesIcon, type VibesIconName } from './VibesIcon';
import '../screens/vibes-v2.css';
import { Icon } from '../ds';
import { icons } from '../icons';
import { c, font } from '../tokens';
import type { User } from '../api';
import type { Screen } from '../App';
import { VibesWordmark } from './VibesWordmark';

const NAV: { key: Screen; label: string; icon: VibesIconName }[] = [
  { key: 'about', label: 'Start Here', icon: 'start' },
  { key: 'catalog', label: 'Apps', icon: 'catalog' },
  { key: 'skills', label: 'Skills', icon: 'skills' },
  { key: 'vibe-check', label: 'Vibe Check', icon: 'idea' },
];

interface SidebarProps {
  screen: Screen;
  user: User | null;
  onNavigate: (screen: Screen) => void;
  onSignOut: () => void;
}

export function Sidebar({ screen, user, onNavigate, onSignOut }: SidebarProps) {
  // App creation and details belong to the Apps destination.
  const active: Screen = ['templates', 'wizard', 'detail'].includes(screen) ? 'catalog' : screen;

  return (
    <aside
      style={{
        width: 248,
        flex: '0 0 248px',
        background: c.sidebar,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 12px',
        gap: 24,
        position: 'sticky',
        top: 0,
        height: '100vh',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 8,
          padding: '4px 8px 0',
          color: c.brand,
        }}
        aria-label="Vibes: Build and share at Abridge"
      >
        <VibesWordmark onClick={() => onNavigate('about')} />
        <span
          style={{
            color: c.sidebarText,
            fontSize: 12.5,
            fontWeight: 500,
            letterSpacing: '0.1px',
            lineHeight: '17px',
            whiteSpace: 'nowrap',
          }}
        >
          Build and share at Abridge
        </span>
      </div>

      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV.filter(item => item.key !== 'skills' || user?.isCdpAdmin === true || user?.isSkillsPilot === true).map(item => {
          const isActive = item.key === active;
          return (
            <button
              key={item.key}
              className="vibes-sidebar-nav"
              type="button"
              onClick={() => onNavigate(item.key)}
              aria-current={isActive ? 'page' : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 10px',
                border: 0,
                borderRadius: 10,
                fontFamily: 'inherit',
                fontSize: 13.5,
                textAlign: 'left',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              <VibesIcon name={item.icon} size={18} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ height: 1, background: c.sidebarRule, margin: '8px 4px' }} />

        <button
          type="button"
          onClick={onSignOut}
          className="vibes-sidebar-signout"
          title="Sign out"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 10px',
            border: 0,
            borderRadius: 10,
            fontFamily: font,
            fontSize: 13.5,
            textAlign: 'left',
            width: '100%',
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: 999,
              background: c.sidebarAvatarBg,
              color: c.sidebarText,
              fontSize: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 20px',
            }}
          >
            {user?.initials ?? '—'}
          </span>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user?.name ?? user?.login ?? 'Not signed in'}
          </span>
          <Icon name={icons.signOut} size="sm" color="currentColor" />
        </button>
      </div>
    </aside>
  );
}
